import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Send, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EASE } from "@/components/Reveal";
import {
  HERO_STAGE_IMAGE,
  REGISTER_URL,
  TELEGRAM_URL,
  scrollToSection,
} from "@/data/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "US$15", label: "Rebate per lot" },
  { value: "20 Lot", label: "Target hadiah termurah" },
  { value: "13", label: "Pilihan hadiah" },
];

export default function Hero() {
  const stageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"],
  });
  const stageY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="beranda" className="relative overflow-hidden pt-28 sm:pt-36">
      {/* stage lighting */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-32 left-1/2 h-[560px] w-[900px] -translate-x-1/2 spotlight-gold blur-2xl" />
        <div className="absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-[#F0B429]/15 blur-[120px]" />
        <div className="absolute -right-40 top-1/3 h-[420px] w-[420px] rounded-full bg-[#00F2B6]/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2B6] sm:text-sm"
            data-testid="hero-eyebrow"
          >
            <span className="mr-2 inline-block h-2 w-2 animate-pulse-dot rounded-full bg-[#F0B429]" />
            Trade & Claim × Self Rebate
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-[clamp(3.4rem,11vw,8.5rem)] font-black uppercase leading-[0.94] tracking-tight text-white"
          >
            <Line delay={0.1}>Trade &</Line>
            <Line delay={0.22}>
              <span className="text-gradient-gold">
                Win
              </span>
            </Line>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            className="mt-6 flex flex-col items-center gap-4"
          >
            <span
              data-testid="hero-offer-pill"
              className="glass inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold text-[#00F2B6] glow-teal sm:text-base"
            >
              <Zap className="h-4 w-4" />
              Rebate US$15 per Lot
            </span>
            <p
              data-testid="hero-subheadline"
              className="text-lg font-semibold text-white sm:text-2xl"
            >
              Trading Sekali. Kumpulkan Rebate. Raih Hadiah.
            </p>
            <p
              data-testid="hero-description"
              className="max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base"
            >
              Trading kamu kini punya nilai lebih. Dapatkan{" "}
              <strong className="font-bold text-[#00F2B6]">Rebate US$15</strong> untuk
              setiap lot yang memenuhi syarat, sekaligus{" "}
              <strong className="font-bold text-[#00F2B6]">
                kumpulkan lot untuk menukarkan berbagai hadiah menarik
              </strong>
              .
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row"
          >
            <Button
              asChild
              data-testid="hero-cta-daftar-button"
              className="h-14 w-full rounded-full bg-[#F0B429] px-8 text-base font-bold text-[#07090E] hover:bg-[#D9A019] glow-gold sm:w-auto"
            >
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                Daftar Sekarang
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-cta-telegram-button"
              className="h-14 w-full rounded-full glass px-8 text-base font-bold text-white hover:border-teal-400/50 sm:w-auto"
            >
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" onClick={() => window.fbq && window.fbq("trackCustom", "TelegramClick")}>
                <Send className="h-4 w-4 text-[#00F2B6]" />
                Tanya Admin untuk Klaim US$15 GRATIS
              </a>
            </Button>
          </motion.div>

          <motion.button
            data-testid="hero-scroll-prizes-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            onClick={() => scrollToSection("#semua-hadiah")}
            className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-white"
          >
            Lihat Semua Hadiah
            <ArrowDown className="h-3.5 w-3.5" />
          </motion.button>
        </div>

        {/* Prize stage */}
        <motion.div
          ref={stageRef}
          initial={{ opacity: 0, y: 70, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
          style={{ y: stageY }}
          className="relative mx-auto mt-14 max-w-6xl sm:mt-20"
        >
          <div
            data-testid="hero-podium-container"
            className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
          >
            <img
              src={HERO_STAGE_IMAGE}
              alt="Panggung hadiah: Mercedes-Benz, motor sport, dan gadget di podium"
              className="aspect-[4/3] w-full object-cover sm:aspect-[3/2]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-transparent to-transparent" />

            <span className="glass absolute left-4 top-4 rounded-full px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#00F2B6] sm:left-6 sm:top-6 sm:text-xs">
              Grand Prize · Mercedes-Benz
            </span>
            <span className="glass absolute right-4 top-4 rounded-full px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#F0B429] sm:right-6 sm:top-6 sm:text-xs">
              16.500 Lot
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="mx-auto mt-10 grid max-w-4xl grid-cols-3 gap-4 pb-16 text-center sm:mt-14"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid="hero-stat-item">
              <p className="font-display text-2xl font-black text-white sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-1 text-[11px] font-medium text-zinc-500 sm:text-sm">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
